import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import LockedPrompt from '@/components/feature/LockedPrompt';
import EmptyState from '@/components/base/EmptyState';
import ProfitCurve, { ProfitReadTime } from '@/components/feature/ProfitCurve';
import { useFollowPerformanceSummary } from '@/lib/useFollowPerformanceSummary';
import { verifiedPnlText, forexApi, type FollowPerformanceSummaryDTO, type PositionClosePreviewDTO, type PositionCloseRequestDTO, type TradePositionDTO } from '@/services/forexApi';

function formatSymbol(symbol: string): string {
  if (!symbol) return '-';
  if (symbol.includes('/')) return symbol;
  if (symbol.length === 6) return `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  if (symbol === 'XAUUSD') return 'XAU/USD';
  return symbol;
}

function formatTime(value: string): string {
  const time = new Date(value);
  if (Number.isNaN(time.getTime())) return value;
  return time.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function PerformanceOverviewCard({
  summary,
  readAt,
  loading,
  error,
  onRefresh,
}: {
  summary: FollowPerformanceSummaryDTO | null;
  readAt: number | null;
  loading: boolean;
  error: boolean;
  onRefresh: () => void;
}) {
  const [hide, setHide] = useState(false);
  const verified = Number.isFinite(summary?.total_pnl) && !!summary?.pnl_currency;

  return (
    <div className="bg-white rounded-3xl p-5" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-foreground-600 text-xs">跟单表现总览</span>
          <button aria-label={hide ? '显示收益' : '隐藏收益'} onClick={() => setHide(!hide)} className="w-4 h-4 flex items-center justify-center">
            <i className={`${hide ? 'ri-eye-off-line' : 'ri-eye-line'} text-foreground-400 text-xs`} />
          </button>
        </div>
        <div className="flex items-center gap-2 text-foreground-600 text-[10px]">
          <span>近 {summary?.period_days ?? 180} 天</span>
          <button type="button" aria-label="刷新收益" disabled={loading} onClick={onRefresh} className="min-h-11 px-2 text-indigo-600 disabled:opacity-50">{loading ? '读取中…' : '刷新'}</button>
        </div>
      </div>

      <div className="flex items-end justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <p className="break-words text-foreground-950 font-black text-2xl leading-tight tracking-tight">
            {hide ? '****' : verified ? verifiedPnlText(summary?.total_pnl, summary?.pnl_currency) : '—'}
          </p>
          <p className="text-green-500 text-sm font-medium mt-1">
            {hide ? '****' : `${summary?.active_follow_relations ?? '—'} 个跟单运行中`}
          </p>
        </div>
        <ProfitCurve summary={summary} compact hidden={hide} loading={loading} error={error} />
      </div>

      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-50">
        <Metric label="胜率" value={hide ? '****' : verified && summary?.win_rate != null ? `${summary.win_rate.toFixed(1)}%` : '待核对'} color="#10B981" />
        <Metric label="最大回撤" value={hide ? '****' : verified && summary?.max_drawdown != null ? `${summary.max_drawdown.toFixed(2)} ${summary.pnl_currency}` : '待核对'} color="#EF4444" />
        <Metric label="已核对平仓" value={hide ? '****' : String(summary?.closed_trade_count ?? '—')} color="#6366F1" />
      </div>
      {!verified && <p className="mt-3 text-xs text-amber-700">平仓盈亏或账户币种尚未核对，暂不计算收益、胜率和回撤。</p>}
      <p className="mt-1 text-xs text-foreground-600">执行记录 {summary?.execution_count ?? '—'} 条 · 待核对平仓 {summary?.unverified_closed_count ?? '—'} 条</p>
      <ProfitReadTime readAt={readAt} stale={error} />
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <p className="text-foreground-600 text-[10px]">{label}</p>
      <p className="font-bold text-sm mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}

function PositionCloseControl({ position, userId, accessToken, onRefresh }: {
  position: TradePositionDTO;
  userId: string;
  accessToken: string;
  onRefresh: () => void;
}) {
  const storageKey = `forex-position-close:${userId}:${position.follower_account_id}:${position.id}`;
  const [preview, setPreview] = useState<PositionClosePreviewDTO | null>(null);
  const [request, setRequest] = useState<PositionCloseRequestDTO | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);

  useEffect(() => {
    const requestId = sessionStorage.getItem(storageKey);
    if (!requestId) return;
    let active = true;
    void forexApi.getPositionCloseRequest(requestId, accessToken)
      .then((value) => { if (active) setRequest(value); })
      .catch(() => { if (active) setError('原平仓请求状态暂不可读，请刷新重试。'); });
    return () => { active = false; };
  }, [storageKey, accessToken]);

  useEffect(() => {
    if (!request || request.terminal) return;
    const timer = window.setInterval(() => {
      void forexApi.getPositionCloseRequest(request.request_id, accessToken)
        .then((value) => {
          setRequest(value);
          if (value.terminal) onRefresh();
        }).catch(() => setError('结果暂不可读，原请求仍在核对中。'));
    }, 5000);
    return () => window.clearInterval(timer);
  }, [request, accessToken, onRefresh]);

  const refreshRequest = async () => {
    const requestId = request?.request_id || sessionStorage.getItem(storageKey);
    if (!requestId) return;
    setLoading(true);
    setError(null);
    try {
      const value = await forexApi.getPositionCloseRequest(requestId, accessToken);
      setRequest(value);
      if (value.terminal) onRefresh();
    } catch {
      setError('原平仓请求状态暂不可读，请稍后重试。');
    } finally {
      setLoading(false);
    }
  };

  const showPreview = async () => {
    if (loading) return;
    setOpen(true);
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const value = await forexApi.previewPositionClose(position.id, accessToken);
      setPreview(value);
      if (value.active_request) {
        sessionStorage.setItem(storageKey, value.active_request.request_id);
        setRequest(await forexApi.getPositionCloseRequest(value.active_request.request_id, accessToken));
      }
    } catch {
      setError('平仓资格暂不可读，请稍后重新查看。');
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    if (submitting.current || !preview?.can_close || preview.remaining_lot == null
        || preview.position_revision == null || !preview.position_snapshot_hash) return;
    submitting.current = true;
    setLoading(true);
    setError(null);
    const requestId = crypto.randomUUID();
    sessionStorage.setItem(storageKey, requestId);
    try {
      const value = await forexApi.submitPositionClose(position.id, {
        request_id: requestId, mode: 'full',
        expected_position_revision: preview.position_revision,
        expected_remaining_lot: preview.remaining_lot,
        position_snapshot_hash: preview.position_snapshot_hash,
        consent_version: preview.consent_version,
      }, accessToken);
      setRequest(value);
      if (value.terminal) onRefresh();
    } catch {
      try {
        const value = await forexApi.getPositionCloseRequest(requestId, accessToken);
        setRequest(value);
      } catch {
        setError('提交结果暂不可读。请查询原请求，勿重复发起平仓。');
      }
    } finally {
      setLoading(false);
      submitting.current = false;
    }
  };

  const canStartAgain = request?.terminal && request.status !== 'closed';
  return (
    <>
      <button type="button" onClick={() => {
        if (canStartAgain) {
          sessionStorage.removeItem(storageKey);
          setRequest(null);
          void showPreview();
        } else if (request || sessionStorage.getItem(storageKey)) {
          setOpen(true);
          void refreshRequest();
        } else {
          void showPreview();
        }
      }} className="mt-1 min-h-11 inline-flex items-center gap-0.5 text-indigo-600 text-xs font-medium"
        aria-label={`查看 ${formatSymbol(position.symbol)} 平仓`}>
        {request && !canStartAgain ? '查看平仓进度' : '查看并平仓'}
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
      </button>
      {request && <p className="text-[10px] text-foreground-600">{request.message}</p>}
      {open && <div role="dialog" aria-modal="true" aria-label="确认平仓"
        className="fixed inset-0 z-[60] bg-black/35 flex items-end sm:items-center justify-center p-3"
        onClick={() => setOpen(false)}>
        <div className="bg-white rounded-2xl p-5 w-full max-w-sm max-h-[calc(100dvh-1.5rem)] overflow-y-auto shadow-xl"
          onClick={(event) => event.stopPropagation()}>
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-foreground-950">{formatSymbol(position.symbol)} 平仓</h2>
            <button type="button" onClick={() => setOpen(false)} aria-label="关闭平仓窗口"
              className="min-w-11 min-h-11 text-foreground-600">关闭</button>
          </div>
          {loading && <p role="status" className="text-sm text-foreground-600 mt-3">正在读取状态…</p>}
          {preview && <div className="text-sm text-foreground-600 mt-3 space-y-2">
            <p>账户 {preview.account.masked} · {preview.position_masked}</p>
            <p>{formatSymbol(preview.symbol)} {preview.side} · 本次将平掉当前剩余 {preview.remaining_lot ?? '待核对'} lot。</p>
            <p>按当前市场报价执行，可能产生滑点或被拒单；结果以 Broker 核实为准。</p>
            <p>{preview.follow_notice}</p>
            {preview.reference_pnl.quality === 'unrealized_excludes_fees'
              ? <p>当前浮动盈亏 {preview.reference_pnl.amount} {preview.reference_pnl.currency}（未计费用），不代表成交收益。</p>
              : <p>浮动盈亏尚未核对，成交盈亏待核对。</p>}
            {!preview.can_close && <p className="text-amber-700">当前不可平仓：{preview.blocking_reasons.map((item) => item.message).join('、')}</p>}
          </div>}
          {request && <div className="mt-3 text-sm text-foreground-950">
            <p>{request.message}</p>
            {request.status === 'partially_closed' && <p>已核实平仓 {request.confirmed_closed_lot} lot，剩余 {request.confirmed_remaining_lot} lot。再次平仓需重新确认。</p>}
            {request.status === 'result_unknown' && <p>Broker 结果尚未核实，请查询原请求。</p>}
            {request.status === 'closed' && (request.pnl?.quality === 'complete'
              ? <p>已核对净盈亏 {request.pnl.amount} {request.pnl.currency}</p>
              : <p>成交已核实，净盈亏待核对。</p>)}
          </div>}
          {error && <p role="alert" className="mt-3 text-sm text-amber-700">{error}</p>}
          <div className="mt-5 flex gap-2">
            <button type="button" onClick={() => setOpen(false)} className="min-h-11 flex-1 rounded-xl border border-gray-200">取消</button>
            {request || sessionStorage.getItem(storageKey) ?
              <button type="button" disabled={loading} onClick={() => void refreshRequest()}
                className="min-h-11 flex-1 rounded-xl bg-indigo-600 text-white disabled:opacity-50">查询原请求</button> :
              <button type="button" disabled={loading || !preview?.can_close} onClick={() => void confirm()}
                className="min-h-11 flex-1 rounded-xl bg-indigo-600 text-white disabled:opacity-50">确认平仓</button>}
          </div>
        </div>
      </div>}
    </>
  );
}

function PositionsPnL({ positions, userId, accessToken, onRefresh }: {
  positions: TradePositionDTO[];
  userId: string;
  accessToken: string;
  onRefresh: () => void;
}) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
      <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
        <span className="text-foreground-950 font-semibold text-sm">持仓浮动盈亏（未计费用）</span>
        <span className="text-foreground-600 text-xs">{positions.length} 笔</span>
      </div>
      {positions.length === 0 ? (
        <div className="px-4 py-6">
          <EmptyState icon="ri-funds-line" title="暂无持仓数据" subtitle="子账号当前没有已关联的开放持仓。" />
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {positions.map((position) => (
            <div key={`${userId}:${position.id}`} className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-foreground-950 text-sm font-semibold">{formatSymbol(position.symbol)}</span>
                  <span className={`text-[10px] font-semibold ${position.action === 'BUY' ? 'text-green-600' : 'text-red-500'}`}>{position.action}</span>
                </div>
                <p className="text-foreground-600 text-[10px] mt-1">{position.lot == null ? '手数待同步' : `${position.lot} lot`} · 开仓 {position.open_price ?? '-'}</p>
                <PositionCloseControl position={position} userId={userId} accessToken={accessToken} onRefresh={onRefresh} />
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-sm font-semibold ${position.pnl == null ? 'text-gray-400' : position.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {verifiedPnlText(position.pnl, position.pnl_currency)}
                </p>
                <p className="text-foreground-600 text-[10px] mt-1">{position.sync_status === 'fresh' ? '现价' : '最近价格'} {position.current_price ?? '待同步'}</p>
                {position.sync_status !== 'fresh' && <p className="text-amber-600 text-[10px] mt-1">持仓状态待确认 · 盈亏可能已过期</p>}
                {position.pnl_as_of != null && <p className="text-foreground-600 text-[10px] mt-1">最近同步于 {new Date(position.pnl_as_of * 1000).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })}（北京时间）</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TradeHistoryList({ items }: { items: TradePositionDTO[] }) {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
      <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
        <span className="text-foreground-950 font-semibold text-sm">交易记录</span>
        <span className="text-foreground-600 text-xs">{items.length} 条</span>
      </div>
      {items.length === 0 && (
        <div className="px-4 py-6 text-center text-foreground-600 text-xs">暂无执行记录</div>
      )}
      <div className="flex flex-col">
        {items.map((item, idx) => {
          const hasPnl = typeof item.pnl === 'number' && !!item.pnl_currency;
          const isProfit = hasPnl && item.pnl! >= 0;
          return (
            <div key={item.id}>
              <div
                className="px-4 py-3 flex items-start gap-3 cursor-pointer active:bg-gray-50 transition-colors"
                onClick={() => navigate('/trade-history')}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ background: !hasPnl ? '#F3F4F6' : isProfit ? '#ECFDF5' : '#FFF1F2' }}
                >
                  <i
                    className={`${!hasPnl ? 'ri-time-line' : isProfit ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-sm`}
                    style={{ color: !hasPnl ? '#9CA3AF' : isProfit ? '#059669' : '#EF4444' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-foreground-950 text-sm font-medium">{formatSymbol(item.symbol)}</span>
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                      style={{
                        background: item.action === 'BUY' ? '#ECFDF5' : '#FFF1F2',
                        color: item.action === 'BUY' ? '#059669' : '#E11D48',
                      }}
                    >
                      {item.action}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-robot-2-line text-foreground-600 text-[10px]" />
                    <span className="text-foreground-600 text-[10px]">Broker 已平仓</span>
                    <span className="text-foreground-400 text-[10px]">·</span>
                    <span className="text-foreground-600 text-[10px]">{item.opened_at ? formatTime(item.opened_at) : '成交时间待核验'}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-[10px] font-medium" style={{ color: '#6366F1' }}>查看交易过程</span>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-[10px]" style={{ color: '#6366F1' }} />
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm font-semibold ${!hasPnl ? 'text-gray-400' : isProfit ? 'text-green-500' : 'text-red-500'}`}>
                    {verifiedPnlText(item.pnl, item.pnl_currency)}
                  </p>
                </div>
              </div>
              {idx < items.length - 1 && <div className="h-px mx-4 bg-gray-50" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AssetsPage() {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const { summary, readAt, loading: summaryLoading, error: summaryError, refresh: refreshSummary } = useFollowPerformanceSummary(accessToken, user?.user_id);
  const [executions, setExecutions] = useState<TradePositionDTO[]>([]);
  const [positions, setPositions] = useState<TradePositionDTO[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(true);
  const [executionsError, setExecutionsError] = useState(false);
  const [executionsRetry, setExecutionsRetry] = useState(0);
  const [positionsLoading, setPositionsLoading] = useState(true);
  const [positionsError, setPositionsError] = useState(false);
  const [positionsRetry, setPositionsRetry] = useState(0);

  useEffect(() => {
    setExecutions([]);
    setExecutionsError(false);
    setPositions([]);
    setPositionsError(false);
  }, [accessToken, user]);

  useEffect(() => {
    if (!accessToken || !user) { setExecutionsLoading(false); return; }
    let active = true;
    setExecutionsLoading(true);
    setExecutionsError(false);
    void forexApi.listClosedTradePositions(accessToken).then((value) => {
      if (active) setExecutions(value.items);
    }).catch(() => { if (active) setExecutionsError(true); })
      .finally(() => { if (active) setExecutionsLoading(false); });
    return () => { active = false; };
  }, [accessToken, user, executionsRetry]);

  useEffect(() => {
    if (!accessToken || !user) { setPositionsLoading(false); return; }
    let active = true;
    setPositionsLoading(true);
    setPositionsError(false);
    void forexApi.listPositions(accessToken).then((value) => {
      if (active) setPositions(value);
    }).catch(() => { if (active) setPositionsError(true); })
      .finally(() => { if (active) setPositionsLoading(false); });
    return () => { active = false; };
  }, [accessToken, user, positionsRetry]);

  const lockedCards = [
    { icon: 'ri-bar-chart-line', title: '跟单表现', desc: '累计参考收益、胜率等指标', color: '#6366f1', bg: '#eef2ff' },
    { icon: 'ri-funds-line', title: '持仓盈亏', desc: '当前浮动盈亏概览', color: '#10b981', bg: '#ecfdf5' },
    { icon: 'ri-file-list-3-line', title: '交易记录', desc: '每笔跟单交易明细', color: '#f59e0b', bg: '#fffbeb' },
  ];

  const recentExecutions = useMemo(() => executions.slice(0, 3), [executions]);

  return (
    <div className="min-h-screen" style={{ background: '#F7F8FC' }}>
      <div className="px-5 pb-2" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }}>
        <h1 className="text-foreground-950 font-bold text-xl">跟单表现</h1>
        <p className="text-foreground-600 text-xs mt-0.5">跟单交易参考表现与持仓盈亏</p>
      </div>

      {!user ? (
        <div className="pb-24">
          <LockedPrompt
            image="https://readdy.ai/api/search-image?query=3D%20minimalist%20purple%20wallet%20icon%20with%20golden%20coins%2C%20soft%20gradient%20from%20light%20purple%20to%20deep%20violet%2C%20clean%20modern%20fintech%20style%2C%20rounded%20corners%2C%20glossy%20surface%2C%20isolated%20on%20pure%20white%20background%2C%20centered%20composition%2C%20high%20quality%203D%20render%2C%20no%20text%2C%20no%20shadows&width=240&height=240&seq=profit_wallet_01&orientation=squarish"
            title="跟单表现已保护"
            subtitle="登录后查看跟单表现与持仓盈亏"
            buttonText="登录查看表现"
            onButtonClick={() => navigate('/auth', { state: { from: '/assets' } })}
          >
            <div className="w-full flex flex-col gap-2.5">
              {lockedCards.map((card) => (
                <div
                  key={card.title}
                  className="bg-white rounded-2xl p-3.5 relative"
                  style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}
                >
                  <i className="ri-lock-line absolute top-3 right-3 text-gray-300 text-xs" />
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: card.bg }}>
                      <i className={`${card.icon} text-base`} style={{ color: card.color }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-foreground-950 font-semibold text-sm">{card.title}</p>
                      <p className="text-foreground-600 text-xs mt-0.5">{card.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </LockedPrompt>
        </div>
      ) : (
        <div className="px-5 pt-2 pb-28 flex flex-col gap-4">
          {summaryLoading && !(summary !== null) && <p role="status" className="text-xs text-gray-500">正在加载收益…</p>}
          {summaryError && <p role="alert" className="text-xs text-amber-700">{summary !== null ? '收益更新失败，显示上次结果。' : '收益加载失败。'} <button onClick={refreshSummary}>重试收益</button></p>}
          <PerformanceOverviewCard summary={summary} readAt={readAt} loading={summaryLoading} error={summaryError} onRefresh={refreshSummary} />

          <button
            onClick={() => navigate('/profit-stats')}
            className="w-full bg-white rounded-2xl p-4 flex items-center gap-3 active:scale-[0.98] transition-transform"
            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: '#EEF2FF' }}>
              <i className="ri-funds-line text-lg" style={{ color: '#6366F1' }} />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-foreground-950 font-semibold text-sm">AI 决策表现</p>
              <p className="text-foreground-600 text-xs mt-0.5">表现归因 · 胜率概览 · 回撤分析</p>
            </div>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-s-line text-foreground-400 text-lg" />
            </div>
          </button>

          {positionsLoading && !(positions.length > 0) && <p role="status" className="text-xs text-gray-500">正在加载持仓…</p>}
          {positionsError && <p role="alert" className="text-xs text-amber-700">{positions.length > 0 ? '持仓更新失败，显示上次结果。' : '持仓加载失败。'} <button onClick={() => setPositionsRetry((key) => key + 1)}>重试持仓</button></p>}
          {(positions.length > 0 || (!positionsLoading && !positionsError)) && <PositionsPnL positions={positions} userId={user.user_id} accessToken={accessToken} onRefresh={() => setPositionsRetry((key) => key + 1)} />}
          {executionsLoading && !(executions.length > 0) && <p role="status" className="text-xs text-gray-500">正在加载平仓记录…</p>}
          {executionsError && <p role="alert" className="text-xs text-amber-700">{executions.length > 0 ? '平仓记录更新失败，显示上次结果。' : '平仓记录加载失败。'} <button onClick={() => setExecutionsRetry((key) => key + 1)}>重试平仓记录</button></p>}
          {(executions.length > 0 || (!executionsLoading && !executionsError)) && <TradeHistoryList items={recentExecutions} />}

          <button
            onClick={() => navigate('/trade-history')}
            className="w-full py-3 rounded-xl border border-dashed flex items-center justify-center gap-1.5 text-sm font-medium transition-all active:scale-[0.98]"
            style={{ borderColor: '#6366F140', color: '#6366F1', background: '#6366F106' }}
          >
            <i className="ri-file-list-3-line" />
            查看全部历史
            <i className="ri-arrow-right-s-line text-base" />
          </button>

          <p className="text-center text-foreground-600 text-xs py-2 leading-relaxed">
            不保证收益，交易风险由用户承担。历史表现不预示未来结果。
          </p>
        </div>
      )}
    </div>
  );
}
